import { Component, computed, inject } from '@angular/core';
import { RouteInjection } from '../../../shared/routing/route-injection.class';
import { Contact, ContactParticipant } from '../../../api/app-types/contact.app';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { DatePipe } from '@angular/common';
import { PERSON_TYPE, personTypeTranslationKeys } from '../../../api/app-types/search.app';
import { SendEmailButton } from '../../../core/components/send-email-button/send-email-button';
import { LvrDocumentItem } from '../../../core/components/document-item/document-item';
import { LvrHeading } from '../../../core/components/heading/heading';
import { calculateRemainingEditDays } from '../../../shared/helpers/remaining-edit-days.func';
import { DOCUMENT_CATEGORY, DocumentEntry, DocumentFile, DocumentNavItem } from '../../../api/app-types/document.app';
import { CarouselElement, NavCarousel } from '../../../core/components/nav-carousel/nav-carousel';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { formatDateToDisplay } from '../../../shared/helpers/date.func';
import { hasPermission } from '../../../shared/permissions/permission.func';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';

@Component({
	selector: 'lvr-network-contact-details',
	imports: [LvrHeading, TranslocoPipe, DatePipe, SendEmailButton, LvrDocumentItem, NavCarousel],
	templateUrl: './network-contact-details.html',
	styleUrl: './network-contact-details.scss',
})
export class NetworkContactDetails extends RouteInjection {
	private readonly env = inject(ENVIRONMENT);
	private readonly transloco = inject(TranslocoService);
	private readonly session = inject(SessionManagerService);
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly contact = this.resolveDataAsSignal<Contact>('contact');
	private readonly contactPage = this.resolveDataAsSignal<PaginatedResponse<Contact>>('contacts');
	public readonly contacts = computed(() => this.contactPage()?.entities ?? ([] as Contact[]));
	public readonly canEditContacts = hasPermission(this.session.permissions, 'Contacts.Edit');

	public readonly carouselElements = computed(() =>
		this.contacts().map((c) => ({
			id: c.id!.toString(),
			label: `${c.keyword}`,
		})),
	);

	public readonly currentIndex = computed(() => {
		const id = this.contact()?.id.toString();
		return this.carouselElements().findIndex((e) => e.id === id);
	});

	public readonly backLink = computed(() =>
		this.router.createUrlTree(['../../../'], { relativeTo: this.route }).toString(),
	);

	public onNavigate(event: { index: number; element: CarouselElement }) {
		this.router.navigate(['../', event.element.id], { relativeTo: this.route });
	}

	public readonly isEditDisabled = computed(() => this.remainingEditDays() < 0);

	public readonly isFrozen = computed(() => this.contact()!.isFrozen);

	public readonly editDisabledReason = computed(() => {
		if (this.isFrozen()) return 'contact.details.edit_disabled.used_in_report';
		if (this.isEditDisabled()) return 'contact.details.edit_disabled.too_old';
		return '';
	});

	public readonly disableEditTranslationParams = computed(() => {
		if (!this.isEditDisabled()) return;

		const limitInDays = this.env.formConfiguration.contact.updateCreatePeriodAsDays;
		return { days: limitInDays };
	});

	public mapPersonTypeToKey(pt: PERSON_TYPE) {
		return personTypeTranslationKeys[pt] || 'unknown';
	}

	public trackItem(item: ContactParticipant) {
		const trackBy = item.item.id + '-' + item.personTypeId;
		return trackBy;
	}

	public isContactTypeAnEmail(contact?: Contact) {
		const remark = contact?.contactType?.remark?.toLowerCase();
		return remark == 'email' || remark == 'e-mail';
	}

	public openEdit() {
		this.router.navigate(['edit'], { relativeTo: this.route });
	}

	public onFileClick(file: DocumentFile) {
		const contact = this.contact();
		if (!contact) return;

		const navItems = this.buildContactNavItems(contact);
		this.router.navigate(['/desk/network-contact', contact.id, 'documents', file.id], {
			state: { navItems: navItems, returnUrl: this.router.url },
		});
	}

	public buildDocumentEntryFromContact(doc: DocumentFile) {
		const contact = this.contact();
		if (!contact) return;

		const createdAt = contact.contactedAt ?? new Date();
		const documentEntry: DocumentEntry = {
			id: contact.id,
			name: doc.name,
			category: DOCUMENT_CATEGORY.CONTACT,
			createdAt: createdAt,
			externalFileReference: contact.documents ?? [],
			isReport: false,
			isFrozen: this.isFrozen() || this.isEditDisabled(),
		};
		return documentEntry;
	}

	private buildContactNavItems(contact: Contact): DocumentNavItem[] {
		const files = contact.documents ?? [];
		const createdAt = contact.contactedAt ?? new Date();
		const entryName = this.transloco.translate('network_contact.details.title', {
			date: formatDateToDisplay(createdAt),
		});

		return files.map((file) => ({
			file,
			entryId: contact.id,
			entryName,
			category: DOCUMENT_CATEGORY.CONTACT,
			createdAt,
			isReport: false,
			isFrozen: this.isFrozen() || this.isEditDisabled(),
		}));
	}

	/**
	 * Computes how many days are left before the contact's edit period expires.
	 * Returns negative values if the deadline has already passed.
	 */
	private readonly remainingEditDays = computed(() => {
		return calculateRemainingEditDays(
			this.contact()!.contactedAt,
			this.env.formConfiguration.contact.updateCreatePeriodAsDays,
		);
	});
}
